'use strict';

/**
 * This module allows defining a custom order for a given list, by assigning a weight for each item.
 * Reordering the an item on the list generally requires updating only the item itself.
 * In certain cases, two items might occupy the same weight, in which case the entire list's
 * weights must be reassigned.
 *
 * Usage example:
 *
 *    const co = new CustomOrder({ weightStep: 10e8 });
 *
 *    let items = [...Array(32)].map((_, index) => String.fromCharCode(index + 65));
 *
 *    items = co.initList(items);
 *
 *    // rearrange the list randomly a few times...
 *    for(let i = 0; i < 3000; i++) {
 *      let to, fr = Math.floor(Math.random() * items.length);
 *      do {
 *        to = Math.floor(Math.random() * items.length)
 *      } while (to == fr)
 *      items = co.moveItem(items, fr, to)
 *    }
 *
 *    console.log(
 *     'Items are sorted by weight',
 *     items == items.sort((a, b) => a.weight - b.weight)
 *    );
 *    console.log(
 *     'Items have duplicate weights',
 *     co._hasDuplicateWeights(items)
 *    );
 *
 * @module CustomOrder
 *
 */

class CustomOrder {
  /**
   * @param {object} [options] -
   * @param {number} [options.weightStep] - Initial spacing between weights. Smaller steps make collisions more likely, but too large steps may overflow
   */
  constructor(options = {}) {
    const defaults = {
      weightStep: 10e6,
      updateItem: function(item) {
        // Triggered when an item's weight changes
      },
      updateAll: function(list) {
        // Triggered when all items' weights change
      },
    };
    Object.assign(this, defaults, options);
  }

  /**
   * Initializes a given list with weights. Assumes the list is already sorted.
   *
   * @param {array} list - The list to be sorted
   * @param {boolean} force - Initialize all list items, regardless of whether they already have a set order
   *
   * @returns {array} - List of objects with a 'weight' key
   */
  initList(list, force) {
    return list.map((item, index) => {
      if(item.hasOwnProperty('weight')) {
        if(!force) {
          return item;
        } else {
          item.weight = (index + 1) * this.weightStep;
          return item;
        }
      }
      return {
        item,
        weight: (index + 1) * this.weightStep
      }
    });
  }

  /**
   * Moves an item to another position within the list, assigns new weights based on neighboring items' weights
   *
   * @param {array} list - The subject list
   * @param {number} oldIndex - The index of the item to be moved
   * @param {number} newIndex - The position to which the item will be inserted at
   *
   * @returns {array} - The subject list, with the item moved
   */
  moveItem(list, oldIndex, newIndex) {
    // Pluck the item from the list
    let subject = list.splice(oldIndex, 1)[0];

    let prev, next;
    // Find neighbboring items' weights
    if(newIndex > 0) {
      prev = list[newIndex - 1].weight;
    } else { prev = 0 }
    if(newIndex < (list.length - 1)) {
      next = list[newIndex].weight;
    } else {
      next = list[list.length - 1].weight + this.weightStep;
    }

    // find a new weight value between the neighbors' weights
    subject.weight = Math.round(prev + (next - prev) / 2);

    // Try nudging the weight if it's colliding
    if(subject.weight === prev) subject.weight++;
    if(subject.weight === next) subject.weight--;

    // Insert the item back into the list
    list.splice(newIndex, 0, subject);

    // Check for duplicate weights and reassign all weights if found.
    if(this._hasDuplicateWeights(list)) {
      list = this.initList(list, true);

      this.updateAll(list);
    } else {
      this.updateItem(subject);
    }

    return list;
  }

  /**
   * Checks whether the list has duplicate weight entries
   *
   * @param {array} list - The list to check
   *
   * @returns {boolean} - Truthy, if duplicate weights were found
   */
  _hasDuplicateWeights(list) {
    // A Set object's values must be unique, hence an array
    // converted to a set is only the same length, if all it's values are unique.
    return (new Set(list.map(i => i.weight))).size !== list.length;
  }
}

module.exports = CustomOrder;
